import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { publicRequestAPI, analyticsAPI } from '../services/api';
import { PRACTICE_AREA_KEYS } from '../constants/practiceAreas';
import { CASE_CATEGORIES as CASE_CATEGORIES_IMPORT } from '../constants/caseCategories';
import PhoneInput from '../components/common/PhoneInput';

const REGIONS = [
  'tashkent_city', 'tashkent', 'andijan', 'fergana', 'namangan',
  'samarkand', 'bukhara', 'navoi', 'kashkadarya', 'surkhandarya',
  'jizzakh', 'sirdarya', 'khorezm', 'karakalpakstan',
];

// Always use Russian labels for DB storage/search
const REGION_LABELS_RU = {
  tashkent_city:  'Тошкент',
  tashkent:       'Ташкентская область',
  andijan:        'Андижанская область',
  fergana:        'Ферганская область',
  namangan:       'Наманганская область',
  samarkand:      'Самаркандская область',
  bukhara:        'Бухарская область',
  navoi:          'Навоийская область',
  kashkadarya:    'Кашкадарьинская область',
  surkhandarya:   'Сурхандарьинская область',
  jizzakh:        'Джизакская область',
  sirdarya:       'Сырдарьинская область',
  khorezm:        'Хорезмская область',
  karakalpakstan: 'Республика Каракалпакстан',
};
// Reverse: Russian label → region key (for display translation)
const REGION_KEY_BY_RU = Object.fromEntries(Object.entries(REGION_LABELS_RU).map(([k, v]) => [v, k]));

const REGION_SUBREGIONS = {
  tashkent: [
    { label: 'Нурафшон шаҳри',      value: 'г. Нурафшон' },
    { label: 'Чирчиқ шаҳри',        value: 'г. Чирчик' },
    { label: 'Олмалиқ шаҳри',       value: 'г. Алмалык' },
    { label: 'Ангрен шаҳри',        value: 'г. Ангрен' },
    { label: 'Бекобод шаҳри',       value: 'г. Бекабад' },
    { label: 'Янгийўл шаҳри',       value: 'г. Янгиюль' },
    { label: 'Зангиота тумани',     value: 'Зангиатинский район' },
    { label: 'Қибрай тумани',       value: 'Кибрайский район' },
    { label: 'Бўстонлиқ тумани',    value: 'Бостанлыкский район' },
    { label: 'Бўка тумани',         value: 'Букинский район' },
    { label: 'Паркент тумани',      value: 'Паркентский район' },
    { label: 'Пскент тумани',       value: 'Пскентский район' },
    { label: 'Тошкент тумани',      value: 'Ташкентский район' },
    { label: 'Ўртачирчиқ тумани',   value: 'Уртачирчикский район' },
    { label: 'Юқоричирчиқ тумани',  value: 'Юкоричирчикский район' },
    { label: 'Оҳангарон шаҳри',     value: 'г. Охангарон' },
    { label: 'Чиноз тумани',        value: 'Чиназский район' },
    { label: 'Оққўрғон тумани',     value: 'Аккурганский район' },
    { label: 'Қуйи Чирчиқ тумани', value: 'Куйичирчикский район' },
  ],
  andijan: [
    { label: 'Андижон шаҳри',        value: 'г. Андижан' },
    { label: 'Андижон тумани',       value: 'Андижанский район' },
    { label: 'Асака тумани',         value: 'Асакинский район' },
    { label: 'Балиқчи тумани',       value: 'Балыкчинский район' },
    { label: 'Булоқбоши тумани',     value: 'Булакбашский район' },
    { label: 'Бўстон тумани',        value: 'Бустанский район' },
    { label: 'Жалақудуқ тумани',     value: 'Джалалкудукский район' },
    { label: 'Избоскан тумани',      value: 'Избасканский район' },
    { label: 'Мархамат тумани',      value: 'Мархаматский район' },
    { label: 'Олтинкўл тумани',      value: 'Алтыкульский район' },
    { label: 'Пахтаобод тумани',     value: 'Пахтаабадский район' },
    { label: 'Қўрғонтепа тумани',    value: 'Кургантепинский район' },
    { label: 'Улуғнор тумани',       value: 'Улугнорский район' },
    { label: 'Хонобод тумани',       value: 'Ханабадский район' },
    { label: 'Хўжаобод тумани',      value: 'Ходжаабадский район' },
    { label: 'Шаҳрихон тумани',      value: 'Шахриханский район' },
  ],
  fergana: [
    { label: 'Фарғона шаҳри',       value: 'г. Фергана' },
    { label: 'Қўқон шаҳри',         value: 'г. Коканд' },
    { label: 'Марғилон шаҳри',      value: 'г. Маргилан' },
    { label: 'Қувасой шаҳри',       value: 'г. Кувасай' },
    { label: 'Қува тумани',         value: 'Кувинский район' },
    { label: 'Риштон тумани',       value: 'Риштанский район' },
    { label: 'Бағдод тумани',       value: 'Багдадский район' },
    { label: 'Бешариқ тумани',      value: 'Бешарыкский район' },
    { label: 'Бувайда тумани',       value: 'Бувайдинский район' },
    { label: 'Данғара тумани',       value: 'Дангаринский район' },
    { label: 'Қуштепа тумани',       value: 'Куштепинский район' },
    { label: 'Сўх тумани',           value: 'Сохский район' },
    { label: 'Тошлоқ тумани',        value: 'Ташлакский район' },
    { label: 'Фурқат тумани',        value: 'Фуркатский район' },
    { label: 'Ёзёвон тумани',        value: 'Язяванский район' },
  ],
  namangan: [
    { label: 'Наманган шаҳри',        value: 'г. Наманган' },
    { label: 'Чуст тумани',          value: 'Чустский район' },
    { label: 'Поп тумани',           value: 'Папский район' },
    { label: 'Косонсой тумани',      value: 'Касансайский район' },
    { label: 'Давлатобод тумани',    value: 'Давлатабадский район' },
    { label: 'Мингбулоқ тумани',     value: 'Мингбулакский район' },
    { label: 'Норин тумани',         value: 'Норинский район' },
    { label: 'Тўрақўрғон тумани',    value: 'Туракурганский район' },
    { label: 'Уйчи тумани',          value: 'Уйчинский район' },
    { label: 'Учқўрғон тумани',      value: 'Учкурганский район' },
    { label: 'Янгиқўрғон тумани',    value: 'Янгикурганский район' },
    { label: 'Янги Наманган тумани', value: 'Янги-Наманганский район' },
    { label: 'Чортоқ тумани',        value: 'Чартакский район' },
  ],
  samarkand: [
    { label: 'Самарқанд шаҳри',     value: 'г. Самарканд' },
    { label: 'Каттақўрғон шаҳри',   value: 'г. Каттакурган' },
    { label: 'Ургут тумани',        value: 'Ургутский район' },
    { label: 'Пайариқ тумани',      value: 'Пайарыкский район' },
    { label: 'Иштихон тумани',      value: 'Иштиханский район' },
    { label: 'Жомбой тумани',       value: 'Джамбайский район' },
    { label: 'Оқдарё тумани',       value: 'Акдарьинский район' },
    { label: 'Булунғур тумани',     value: 'Булунгурский район' },
    { label: 'Қўшрабод тумани',     value: 'Кошрабадский район' },
    { label: 'Нарпай тумани',       value: 'Нарпайский район' },
    { label: 'Нуробод тумани',      value: 'Нурабадский район' },
    { label: 'Пастдарғом тумани',   value: 'Пастдаргомский район' },
    { label: 'Пахтачи тумани',      value: 'Пахтачийский район' },
    { label: 'Тайлоқ тумани',       value: 'Тайлакский район' },
  ],
  bukhara: [
    { label: 'Бухоро шаҳри',        value: 'г. Бухара' },
    { label: 'Когон тумани',         value: 'г. Каган' },
    { label: 'Ромитан тумани',      value: 'Ромитанский район' },
    { label: 'Қоракўл тумани',      value: 'Каракульский район' },
    { label: 'Ғиждувон тумани',     value: 'Гиждуванский район' },
    { label: 'Вобкент тумани',      value: 'Вабкентский район' },
    { label: 'Жондор тумани',       value: 'Джандарский район' },
    { label: 'Қоровулбозор тумани', value: 'Каравулбазарский район' },
    { label: 'Олот тумани',         value: 'Алатский район' },
    { label: 'Пешку тумани',        value: 'Пешкунский район' },
    { label: 'Шофиркон тумани',     value: 'Шафирканский район' },
  ],
  navoi: [
    { label: 'Навоий шаҳри',        value: 'г. Навои' },
    { label: 'Зарафшон шаҳри',      value: 'г. Зарафшан' },
    { label: 'Учқудуқ тумани',      value: 'Учкудукский район' },
    { label: 'Нурота тумани',       value: 'Нуратинский район' },
    { label: 'Ғозғон шаҳри',        value: 'Газганский район' },
    { label: 'Конимех тумани',      value: 'Конимехский район' },
    { label: 'Кармана тумани',      value: 'Карманинский район' },
    { label: 'Қизилтепа тумани',    value: 'Кызылтепинский район' },
    { label: 'Навбаҳор тумани',     value: 'Навбахорский район' },
    { label: 'Томди тумани',        value: 'Томдинский район' },
    { label: 'Хатирчи тумани',      value: 'Хатырчинский район' },
  ],
  kashkadarya: [
    { label: 'Қарши шаҳри',         value: 'г. Карши' },
    { label: 'Шаҳрисабз шаҳри',     value: 'г. Шахрисабз' },
    { label: 'Қарши тумани',        value: 'Каршинский район' },
    { label: 'Китоб тумани',        value: 'Китабский район' },
    { label: 'Кўкдала тумани',      value: 'Кукдалинский район' },
    { label: 'Косон тумани',        value: 'Касанский район' },
    { label: 'Муборак тумани',      value: 'Мубарекский район' },
    { label: 'Миришкор тумани',     value: 'Миришкорский район' },
    { label: 'Ғузор тумани',        value: 'Гузарский район' },
    { label: 'Деҳқонобод тумани',   value: 'Дехканабадский район' },
    { label: 'Қамаши тумани',       value: 'Камашинский район' },
    { label: 'Қасби тумани',        value: 'Касбинский район' },
    { label: 'Нишон тумани',        value: 'Нишанский район' },
    { label: 'Чироқчи тумани',      value: 'Чиракчинский район' },
    { label: 'Яккабоғ тумани',      value: 'Яккабагский район' },
  ],
  surkhandarya: [
    { label: 'Термиз шаҳри',        value: 'г. Термез' },
    { label: 'Ангор тумани',        value: 'Ангорский район' },
    { label: 'Денов тумани',        value: 'Денауский район' },
    { label: 'Шеробод тумани',      value: 'Шерабадский район' },
    { label: 'Бойсун тумани',       value: 'Байсунский район' },
    { label: 'Бандихон тумани',     value: 'Бандиханский район' },
    { label: 'Жарқўрғон тумани',    value: 'Джаркурганский район' },
    { label: 'Қизириқ тумани',      value: 'Кизирикский район' },
    { label: 'Қумқўрғон тумани',    value: 'Кумкурганский район' },
    { label: 'Музработ тумани',     value: 'Музрабадский район' },
    { label: 'Олтинсой тумани',     value: 'Алтынсайский район' },
    { label: 'Сариосиё тумани',     value: 'Сариасийский район' },
    { label: 'Узун тумани',         value: 'Узунский район' },
    { label: 'Шўрчи тумани',        value: 'Шурчинский район' },
  ],
  jizzakh: [
    { label: 'Жиззах шаҳри',        value: 'г. Джизак' },
    { label: 'Зомин тумани',        value: 'Заминский район' },
    { label: 'Ғаллаорол тумани',    value: 'Галляаральский район' },
    { label: 'Янгиобод тумани',     value: 'Янгиабадский район' },
    { label: 'Арнасой тумани',      value: 'Арнасайский район' },
    { label: 'Бахмал тумани',       value: 'Бахмалский район' },
    { label: 'Дўстлик тумани',      value: 'Дустликский район' },
    { label: 'Зарбдор тумани',      value: 'Зарбдарский район' },
    { label: 'Зафаробод тумани',    value: 'Зафарабадский район' },
    { label: 'Мирзачўл тумани',     value: 'Мирзачульский район' },
    { label: 'Пахтакор тумани',     value: 'Пахтакорский район' },
    { label: 'Фориш тумани',        value: 'Фаришский район' },
    { label: 'Шароф Рашидов тумани',value: 'Шараф-Рашидовский район' },
  ],
  sirdarya: [
    { label: 'Гулистон шаҳри',      value: 'г. Гулистан' },
    { label: 'Янгиер шаҳри',        value: 'г. Янгиер' },
    { label: 'Хавос тумани',        value: 'Хавастский район' },
    { label: 'Сардоба тумани',      value: 'Сардобинский район' },
    { label: 'Оқолтин тумани',      value: 'Акалтынский район' },
    { label: 'Боёвут тумани',       value: 'Баяутский район' },
    { label: 'Мирзаобод тумани',    value: 'Мирзаабадский район' },
    { label: 'Сайхунобод тумани',   value: 'Сайхунабадский район' },
    { label: 'Ширин шаҳри',         value: 'г. Ширин' },
    { label: 'Сирдарё тумани',      value: 'Сырдарьинский район' },
  ],
  khorezm: [
    { label: 'Урганч шаҳри',        value: 'г. Ургенч' },
    { label: 'Хива шаҳри',          value: 'г. Хива' },
    { label: 'Питнак шаҳри',        value: 'г. Питнак' },
    { label: 'Хонқа тумани',        value: 'Ханкинский район' },
    { label: 'Шовот тумани',        value: 'Шаватский район' },
    { label: 'Боғот тумани',        value: 'Багатский район' },
    { label: 'Гурлан тумани',       value: 'Гурленский район' },
    { label: 'Қўшкўпир тумани',     value: 'Кушкупырский район' },
    { label: 'Хазорасп тумани',     value: 'Хазараспский район' },
    { label: 'Янгиариқ тумани',     value: 'Янгиарыкский район' },
    { label: 'Янгибозор тумани',    value: 'Янгибазарский район' },
  ],
  karakalpakstan: [
    { label: 'Нукус шаҳри',         value: 'г. Нукус' },
    { label: 'Тахиатош шаҳри',      value: 'г. Тахиаташ' },
    { label: 'Тўрткўл шаҳри',       value: 'г. Турткуль' },
    { label: 'Хўжайли шаҳри',       value: 'г. Ходжейли' },
    { label: 'Чимбой шаҳри',        value: 'г. Чимбай' },
    { label: 'Беруний тумани',      value: 'Берунийский район' },
    { label: 'Қўнғирот тумани',     value: 'Кунградский район' },
    { label: 'Амударё тумани',      value: 'Амударьинский район' },
    { label: 'Кегейли тумани',      value: 'Кегейлийский район' },
    { label: 'Қонликўл тумани',     value: 'Канликольский район' },
    { label: 'Мўйноқ тумани',       value: 'Муйнакский район' },
    { label: 'Тахиатош тумани',     value: 'Тахиатошский район' },
    { label: 'Тахтакўпир тумани',   value: 'Тахтакупырский район' },
    { label: 'Шўманай тумани',      value: 'Шуманайский район' },
    { label: 'Элликқалъа тумани',   value: 'Элликкалинский район' },
  ],
};

const CASE_CATEGORIES = CASE_CATEGORIES_IMPORT;


const UzbekIllustration = () => (
  <svg viewBox="0 0 480 420" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: '480px', display: 'block' }}>
    <defs>
      <radialGradient id="illBg" cx="50%" cy="45%" r="55%">
        <stop offset="0%" stopColor="#dbeafe" />
        <stop offset="100%" stopColor="#bfdbfe" stopOpacity="0.3" />
      </radialGradient>
      <linearGradient id="archGrad" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1e3a5f" />
        <stop offset="100%" stopColor="#2c5282" />
      </linearGradient>
    </defs>

    {/* Background circle */}
    <circle cx="240" cy="210" r="195" fill="url(#illBg)" />

    {/* Uzbek arch building (Registan-inspired) */}
    <rect x="115" y="148" width="250" height="232" rx="4" fill="url(#archGrad)" />
    {/* Arch opening cutout */}
    <path d="M 140 148 Q 240 55 340 148 L 340 380 L 140 380 Z" fill="#1e4a8a" />
    {/* Sky inside arch */}
    <path d="M 158 160 Q 240 78 322 160 L 322 380 L 158 380 Z" fill="#e8f4fd" />
    {/* Gold arch border */}
    <path d="M 140 148 Q 240 55 340 148" fill="none" stroke="#d4a843" strokeWidth="4" />
    <path d="M 153 154 Q 240 68 327 154" fill="none" stroke="#f0c040" strokeWidth="1.5" strokeDasharray="5 3" />
    {/* Tile border strips on sides */}
    <rect x="115" y="148" width="25" height="232" fill="#16325a" />
    <rect x="340" y="148" width="25" height="232" fill="#16325a" />
    {/* Gold tile dots on borders */}
    {[170, 200, 230, 260, 290, 320, 350].map((y, i) => (
      <g key={i}>
        <circle cx="127" cy={y} r="3" fill="#d4a843" />
        <circle cx="353" cy={y} r="3" fill="#d4a843" />
      </g>
    ))}
    {/* Star in arch apex */}
    <g transform="translate(228, 82)">
      <polygon points="12,0 14,8 22,8 16,13 18,21 12,17 6,21 8,13 2,8 10,8" fill="#d4a843" />
    </g>
    {/* Arch spandrel geometric patterns */}
    <circle cx="175" cy="175" r="14" fill="none" stroke="#d4a843" strokeWidth="1.5" />
    <circle cx="175" cy="175" r="7" fill="#d4a843" opacity="0.5" />
    <circle cx="305" cy="175" r="14" fill="none" stroke="#d4a843" strokeWidth="1.5" />
    <circle cx="305" cy="175" r="7" fill="#d4a843" opacity="0.5" />

    {/* Table / desk surface */}
    <rect x="142" y="298" width="196" height="11" fill="#7b5e2a" rx="3" />
    <rect x="162" y="309" width="8" height="55" fill="#5a4420" rx="2" />
    <rect x="310" y="309" width="8" height="55" fill="#5a4420" rx="2" />

    {/* Document on desk */}
    <rect x="186" y="258" width="108" height="43" fill="white" rx="5" stroke="#d0d7e3" strokeWidth="1" />
    <line x1="197" y1="270" x2="283" y2="270" stroke="#b0b8c8" strokeWidth="1.5" />
    <line x1="197" y1="280" x2="274" y2="280" stroke="#b0b8c8" strokeWidth="1.5" />
    <line x1="197" y1="290" x2="278" y2="290" stroke="#b0b8c8" strokeWidth="1.5" />
    {/* Pen */}
    <line x1="268" y1="294" x2="292" y2="299" stroke="#3182ce" strokeWidth="3" strokeLinecap="round" />
    <circle cx="267" cy="294" r="2.5" fill="#3182ce" />

    {/* ── Person LEFT: Client with tubeteika ── */}
    {/* Body – traditional blue chapan */}
    <rect x="148" y="233" width="50" height="66" fill="#3a7bd5" rx="8" />
    {/* Chapan gold centre line */}
    <line x1="173" y1="233" x2="173" y2="299" stroke="#d4a843" strokeWidth="2" />
    {/* Head */}
    <circle cx="173" cy="220" r="21" fill="#e8a87c" />
    {/* Tubeteika (Uzbek skullcap) */}
    <ellipse cx="173" cy="204" rx="17" ry="7" fill="#1a365d" />
    <path d="M 156 204 Q 173 195 190 204" fill="#1a365d" />
    {/* Geometric pattern on cap */}
    <text x="163" y="206" fontSize="7" fill="#d4a843">✦✦✦</text>

    {/* ── Person RIGHT: Attorney in suit ── */}
    {/* Body – dark suit */}
    <rect x="282" y="233" width="50" height="66" fill="#1a365d" rx="8" />
    {/* White shirt */}
    <rect x="297" y="233" width="16" height="38" fill="white" rx="2" />
    {/* Tie */}
    <polygon points="305,238 302,250 305,264 308,250" fill="#c53030" />
    {/* Collar */}
    <path d="M 297 233 L 303 244 L 305 233 L 307 244 L 313 233" fill="none" stroke="#e2e8f0" strokeWidth="1" />
    {/* Head */}
    <circle cx="307" cy="220" r="21" fill="#c87941" />
    {/* Hair */}
    <path d="M 286 215 Q 307 197 328 215" fill="#2d1a0a" />
    {/* Briefcase */}
    <rect x="286" y="298" width="42" height="28" fill="#1a2f4a" rx="4" />
    <rect x="302" y="293" width="12" height="10" fill="none" stroke="#1a2f4a" strokeWidth="2.5" rx="3" />
    <line x1="286" y1="310" x2="328" y2="310" stroke="#2c4f74" strokeWidth="1" />

    {/* Legal scales – floating */}
    <g transform="translate(215, 195)">
      <line x1="25" y1="5" x2="25" y2="55" stroke="#d4a843" strokeWidth="2.5" />
      <circle cx="25" cy="5" r="3.5" fill="#d4a843" />
      <line x1="4" y1="22" x2="46" y2="22" stroke="#d4a843" strokeWidth="2.5" />
      {/* Left pan */}
      <line x1="4" y1="22" x2="0" y2="40" stroke="#d4a843" strokeWidth="1.5" />
      <line x1="4" y1="22" x2="10" y2="40" stroke="#d4a843" strokeWidth="1.5" />
      <path d="M -3 40 Q 5 47 12 40" fill="none" stroke="#d4a843" strokeWidth="2" />
      {/* Right pan */}
      <line x1="46" y1="22" x2="40" y2="40" stroke="#d4a843" strokeWidth="1.5" />
      <line x1="46" y1="22" x2="52" y2="40" stroke="#d4a843" strokeWidth="1.5" />
      <path d="M 37 40 Q 46 47 54 40" fill="none" stroke="#d4a843" strokeWidth="2" />
    </g>

    {/* Decorative stars */}
    <text x="62" y="88" fontSize="22" fill="#d4a843" opacity="0.55">★</text>
    <text x="390" y="92" fontSize="17" fill="#d4a843" opacity="0.5">★</text>
    <text x="420" y="168" fontSize="11" fill="#d4a843" opacity="0.4">✦</text>
    <text x="48" y="195" fontSize="13" fill="#3182ce" opacity="0.35">✦</text>

    {/* Green checkmark badge */}
    <circle cx="376" cy="328" r="17" fill="#38a169" opacity="0.88" />
    <path d="M 368 328 L 374 335 L 386 319" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />

    {/* Location pin */}
    <g transform="translate(90, 260)" opacity="0.75">
      <path d="M 10 0 C 4.5 0 0 4.5 0 10 C 0 17.5 10 26 10 26 C 10 26 20 17.5 20 10 C 20 4.5 15.5 0 10 0Z" fill="#e53e3e" />
      <circle cx="10" cy="10" r="4" fill="white" />
    </g>
  </svg>
);

const StepCard = ({ icon, number, title, desc }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '2.5rem 2rem',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  }}>
    <div style={{
      position: 'absolute', top: '1rem', right: '1.25rem',
      fontSize: '3.5rem', fontWeight: '900', color: '#edf2f7', lineHeight: 1,
    }}>{number}</div>
    <div style={{
      width: '72px', height: '72px', borderRadius: '50%',
      backgroundColor: '#ebf8ff', display: 'flex', alignItems: 'center',
      justifyContent: 'center', margin: '0 auto 1.25rem', fontSize: '2rem',
    }}>{icon}</div>
    <h3 style={{ fontSize: '1.15rem', fontWeight: '700', color: '#1a365d', marginBottom: '0.75rem' }}>{title}</h3>
    <p style={{ color: '#718096', fontSize: '0.92rem', lineHeight: '1.65' }}>{desc}</p>
  </div>
);

const RegionDropdown = ({ regions, value, onChange, language, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const label = value || (language === 'ru' ? 'Регион' : 'Region');

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center', padding: '0 0.5rem', flexShrink: 0 }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#718096" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.25rem', flexShrink: 0 }}>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/>
      </svg>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.3rem',
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '0.88rem', color: value ? '#2d3748' : '#a0aec0',
          padding: '0', whiteSpace: 'nowrap', minWidth: '150px',
        }}
      >
        {label}
        <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.5, flexShrink: 0 }}>
          <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          right: '0',
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
          border: '1px solid #e2e8f0',
          minWidth: '200px',
          maxHeight: '260px',
          overflowY: 'auto',
          zIndex: 2000,
          padding: '0.5rem 0',
        }}>
          <RegionItem active={!value} onClick={() => { onChange(''); setOpen(false); }}>
            {language === 'ru' ? 'Все регионы' : 'All regions'}
          </RegionItem>
          {regions.map(key => (
            <RegionItem key={key} active={value === t(`regions.${key}`)} onClick={() => { onChange(t(`regions.${key}`)); setOpen(false); }}>
              {t(`regions.${key}`)}
            </RegionItem>
          ))}
        </div>
      )}
    </div>
  );
};

const RegionItem = ({ onClick, active, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '0.6rem 1.25rem',
        fontSize: '0.9rem',
        cursor: 'pointer',
        color: active ? '#2b6cb0' : '#2d3748',
        fontWeight: active ? '600' : '400',
        backgroundColor: hover ? '#f7fafc' : 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </div>
  );
};

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, isClient, isAttorney } = useAuth();
  const { t, language } = useLanguage();
  const screenWidth = useWindowWidth();
  const isMobile = screenWidth < 768;
  const isTablet = screenWidth < 1024;
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [findMeArea, setFindMeArea] = useState('');
  const [findMeLocation, setFindMeLocation] = useState('');
  const [findMePhone, setFindMePhone] = useState('');
  const [findMeAreaOpen, setFindMeAreaOpen] = useState(false);
  const [findMeAreaExpanded, setFindMeAreaExpanded] = useState(null);
  const [findMeLocationOpen, setFindMeLocationOpen] = useState(false);
  const [findMeLocationExpanded, setFindMeLocationExpanded] = useState(null);
  const [findMeErrors, setFindMeErrors] = useState({});
  const [findMeSuccess, setFindMeSuccess] = useState(false);
  const findMeAreaRef = useRef(null);
  const findMeLocationRef = useRef(null);
  const [locationOpen, setLocationOpen] = useState(false);
  const [subRegionParent, setSubRegionParent] = useState(null);
  const locationRef = useRef(null);
  const [caseAreaOpen, setCaseAreaOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const caseAreaRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target)) { setLocationOpen(false); setSubRegionParent(null); }
      if (caseAreaRef.current && !caseAreaRef.current.contains(e.target)) { setCaseAreaOpen(false); setExpandedCategory(null); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (findMeAreaRef.current && !findMeAreaRef.current.contains(e.target)) { setFindMeAreaOpen(false); setFindMeAreaExpanded(null); }
      if (findMeLocationRef.current && !findMeLocationRef.current.contains(e.target)) setFindMeLocationOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleFindMe = async () => {
    const errors = {};
    if (!findMeArea) errors.area = true;
    if (!findMeLocation) errors.location = true;
    if (!findMePhone.trim()) errors.phone = true;
    if (Object.keys(errors).length > 0) { setFindMeErrors(errors); return; }
    setFindMeErrors({});
    try {
      await publicRequestAPI.submit({ practiceArea: findMeArea, location: findMeLocation, phone: findMePhone });
    } catch (_) {}
    setFindMeSuccess(true);
  };

  const handleSearch = () => {
    if (!selectedArea || !selectedLocation) return;
    analyticsAPI.searchClick(selectedArea, selectedLocation);
    navigate('/find-attorney', { state: { practiceArea: selectedArea, location: selectedLocation } });
  };

  const handleAttorneyClick = () => {
    if (user && isAttorney) navigate('/attorney/dashboard');
    else navigate('/register', { state: { userType: 'attorney' } });
  };

  const handleClientStart = () => {
    navigate('/find-attorney');
  };

  return (
    <div style={{ backgroundColor: '#f7fafc' }}>

      {/* ══════════════ HERO ══════════════ */}
      <section style={{
        background: 'linear-gradient(135deg, #1a365d 0%, #2a5298 60%, #2b6cb0 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: isMobile ? '2rem 1rem 3rem' : '3rem 2rem 4rem',
        minHeight: isMobile ? 'auto' : '82vh',
        display: 'flex',
        alignItems: 'center',
      }}>
        {/* Subtle background circles */}
        <div style={{
          position: 'absolute', top: '-100px', right: '-100px',
          width: '500px', height: '500px', borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.04)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-150px', left: '-80px',
          width: '400px', height: '400px', borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.03)',
          pointerEvents: 'none',
        }} />

        {/* "I am attorney" — hidden for attorneys, logged-in clients, and on mobile */}
        {!isAttorney && !isClient && !isMobile && <button
          onClick={handleAttorneyClick}
          style={{
            position: 'absolute', top: '1.5rem', right: '2rem',
            backgroundColor: 'transparent',
            color: 'white',
            border: '1.5px solid rgba(255,255,255,0.55)',
            padding: '0.55rem 1.25rem',
            borderRadius: '25px',
            fontSize: '0.9rem',
            fontWeight: '500',
            cursor: 'pointer',
            letterSpacing: '0.02em',
            transition: 'all 0.2s',
            zIndex: 10,
          }}
          onMouseOver={e => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)';
            e.currentTarget.style.borderColor = 'white';
          }}
          onMouseOut={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.55)';
          }}
        >
          {t('landing.amAttorneyBtn')} →
        </button>}

        {/* Two-column layout */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          padding: isMobile ? '0 0.5rem' : '0 3rem',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '2rem' : '4rem',
          alignItems: 'flex-start',
        }}>

          {/* ── LEFT: case category dropdown + location + search ── */}
          <div style={{ paddingTop: isMobile ? '0' : '3rem' }}>
            {/* Headline */}
            <div style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: '800', color: 'white', marginBottom: '1.5rem', lineHeight: 1.15, letterSpacing: '-0.01em' }}>
              {language === 'ru' ? 'Найдите адвоката' : 'Адвокат топинг'}
            </div>
            {/* Case category dropdown */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: '600', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
                Муносабатни танланг
              </div>
              <div ref={caseAreaRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => { setCaseAreaOpen(o => !o); setExpandedCategory(null); }}
                  style={{
                    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.75rem 1.1rem',
                    borderRadius: '50px',
                    backgroundColor: 'white',
                    border: '2px solid #cbd5e0',
                    color: selectedArea ? '#1a365d' : '#718096',
                    fontSize: '0.95rem', fontWeight: '500', cursor: 'pointer',
                    transition: 'all 0.15s', textAlign: 'left',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedArea || 'Масалан, оила ҳуқуқи ...'}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0, marginLeft: '0.5rem' }}>
                    {selectedArea && (
                      <span
                        onClick={e => { e.stopPropagation(); setSelectedArea(''); }}
                        style={{ opacity: 0.7, fontSize: '1.1rem', lineHeight: 1 }}
                      >×</span>
                    )}
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ opacity: 0.6, transform: caseAreaOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </button>

                {caseAreaOpen && (
                  <div className="case-area-dropdown" style={{
                    position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0,
                    backgroundColor: 'white', borderRadius: '10px',
                    boxShadow: '0 -4px 32px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0',
                    maxHeight: '220px', overflowY: 'auto', zIndex: 2000, padding: '0.4rem 0',
                  }}>
                    <style>{`
                      .case-area-dropdown::-webkit-scrollbar { width: 5px; }
                      .case-area-dropdown::-webkit-scrollbar-track { background: transparent; }
                      .case-area-dropdown::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
                    `}</style>
                    {CASE_CATEGORIES.map((cat, ci) => (
                      <React.Fragment key={ci}>
                        <div
                          onClick={() => {
                            if (cat.subs.length === 0) {
                              setSelectedArea(cat.label); setCaseAreaOpen(false); setExpandedCategory(null);
                            } else {
                              setExpandedCategory(p => p === ci ? null : ci);
                            }
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '0.6rem 1.1rem', fontSize: '0.9rem', cursor: 'pointer',
                            color: (expandedCategory === ci || selectedArea === cat.label) ? '#2b6cb0' : '#1a202c',
                            fontWeight: '600',
                            backgroundColor: (expandedCategory === ci || selectedArea === cat.label) ? '#ebf8ff' : 'transparent',
                          }}
                          onMouseEnter={e => { if (expandedCategory !== ci && selectedArea !== cat.label) e.currentTarget.style.backgroundColor = '#f7fafc'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = (expandedCategory === ci || selectedArea === cat.label) ? '#ebf8ff' : 'transparent'; }}
                        >
                          <span>{cat.label}</span>
                          {cat.subs.length > 0 && (
                            <svg width="10" height="6" viewBox="0 0 10 6" fill="none"
                              stroke="#a0aec0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                              style={{ transform: expandedCategory === ci ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}
                            >
                              <path d="M1 1l4 4 4-4"/>
                            </svg>
                          )}
                        </div>
                        {expandedCategory === ci && cat.subs.map((sub, si) => (
                          <div
                            key={si}
                            onClick={() => { setSelectedArea(sub); setCaseAreaOpen(false); setExpandedCategory(null); }}
                            style={{
                              padding: '0.48rem 1.1rem 0.48rem 2rem',
                              fontSize: '0.86rem', cursor: 'pointer',
                              color: selectedArea === sub ? '#2b6cb0' : '#4a5568',
                              fontWeight: selectedArea === sub ? '600' : '400',
                              borderLeft: '2px solid #bee3f8', marginLeft: '1.1rem',
                              backgroundColor: selectedArea === sub ? '#ebf8ff' : 'transparent',
                            }}
                            onMouseEnter={e => { if (selectedArea !== sub) e.currentTarget.style.backgroundColor = '#f7fafc'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = selectedArea === sub ? '#ebf8ff' : 'transparent'; }}
                          >
                            {sub}
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Location + Search row */}
            <div style={{ display: 'flex', gap: isMobile ? '0' : '0.75rem', alignItems: 'center', flexWrap: 'nowrap',
              ...(isMobile ? { border: '2px solid #cbd5e0', borderRadius: '50px', overflow: 'visible', backgroundColor: 'white' } : {})
            }}>
              {/* Location dropdown */}
              <div ref={locationRef} style={{ position: 'relative', flex: isMobile ? '1 1 auto' : '0 0 auto' }}>
                <button
                  type="button"
                  onClick={() => { setLocationOpen(o => !o); if (locationOpen) setSubRegionParent(null); }}
                  style={isMobile ? {
                    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                    backgroundColor: 'white', border: 'none',
                    borderRight: '1px solid #e2e8f0',
                    borderRadius: '0', padding: '0.75rem 0.9rem',
                    fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer',
                    whiteSpace: 'nowrap', width: '100%', color: selectedLocation ? '#1a365d' : '#718096',
                    transition: 'all 0.15s',
                  } : {
                    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                    backgroundColor: 'white',
                    color: selectedLocation ? '#1a365d' : '#718096',
                    border: '2px solid #cbd5e0',
                    borderRadius: '50px', padding: '0.7rem 1.1rem',
                    fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer',
                    whiteSpace: 'nowrap', transition: 'all 0.15s',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/>
                  </svg>
                  {selectedLocation
                    ? (REGION_KEY_BY_RU[selectedLocation]
                        ? t(`regions.${REGION_KEY_BY_RU[selectedLocation]}`)
                        : (() => { for (const subs of Object.values(REGION_SUBREGIONS)) { const m = subs.find(s => s.value === selectedLocation); if (m) return language === 'ru' ? m.value : m.label; } return selectedLocation; })())
                    : (language === 'ru' ? 'Выбрать регион' : 'Ҳудудни танланг')
                  }
                  {selectedLocation ? (
                    <span
                      onClick={e => { e.stopPropagation(); setSelectedLocation(''); setLocationOpen(false); }}
                      style={{ marginLeft: '0.1rem', opacity: 0.7, fontSize: '1rem', lineHeight: 1 }}
                    >×</span>
                  ) : (
                    <svg width="9" height="5" viewBox="0 0 10 6" style={{ opacity: 0.5 }}>
                      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>

                {locationOpen && (
                  <div className="location-dropdown" style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 6px)',
                    left: 0,
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    border: '1px solid #e2e8f0',
                    minWidth: '320px',
                    maxHeight: '280px',
                    overflowY: 'auto',
                    zIndex: 2000,
                    padding: '0.5rem 0',
                  }}>
                    <style>{`
                      .location-dropdown::-webkit-scrollbar { width: 5px; }
                      .location-dropdown::-webkit-scrollbar-track { background: transparent; margin: 24px 0; }
                      .location-dropdown::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
                      .location-dropdown::-webkit-scrollbar-thumb:hover { background: #a0aec0; }
                    `}</style>
                    {REGIONS.map(key => (
                      <React.Fragment key={key}>
                        <div
                          onClick={() => {
                            if (REGION_SUBREGIONS[key]?.length) {
                              setSubRegionParent(p => p === key ? null : key);
                            } else {
                              setSelectedLocation(REGION_LABELS_RU[key]);
                              setLocationOpen(false);
                              setSubRegionParent(null);
                            }
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '0.6rem 1.25rem',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            color: (selectedLocation === REGION_LABELS_RU[key] || subRegionParent === key) ? '#2b6cb0' : '#2d3748',
                            fontWeight: (selectedLocation === REGION_LABELS_RU[key] || subRegionParent === key) ? '600' : '400',
                            backgroundColor: subRegionParent === key ? '#ebf8ff' : 'transparent',
                          }}
                          onMouseEnter={e => { if (subRegionParent !== key) e.currentTarget.style.backgroundColor = '#f7fafc'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = subRegionParent === key ? '#ebf8ff' : 'transparent'; }}
                        >
                          <span>{t(`regions.${key}`)}</span>
                          {REGION_SUBREGIONS[key]?.length > 0 && (
                            <svg
                              width="10" height="10" viewBox="0 0 10 6" fill="none"
                              stroke="#a0aec0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                              style={{ transform: subRegionParent === key ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                            >
                              <path d="M1 1l4 4 4-4"/>
                            </svg>
                          )}
                        </div>
                        {subRegionParent === key && (
                          <div
                            onClick={() => { setSelectedLocation(REGION_LABELS_RU[key]); setLocationOpen(false); setSubRegionParent(null); }}
                            style={{
                              padding: '0.5rem 1.25rem 0.5rem 2.25rem',
                              fontSize: '0.86rem', cursor: 'pointer',
                              color: selectedLocation === REGION_LABELS_RU[key] ? '#2b6cb0' : '#4a5568',
                              fontWeight: selectedLocation === REGION_LABELS_RU[key] ? '600' : '500',
                              borderLeft: '2px solid #bee3f8', marginLeft: '1.25rem',
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7fafc'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {language === 'ru' ? `Весь ${t(`regions.${key}`)}` : `${t(`regions.${key}`)} (барчаси)`}
                          </div>
                        )}
                        {subRegionParent === key && REGION_SUBREGIONS[key].map(sub => (
                          <div
                            key={sub.value}
                            onClick={() => { setSelectedLocation(sub.value); setLocationOpen(false); setSubRegionParent(null); }}
                            style={{
                              padding: '0.5rem 1.25rem 0.5rem 2.25rem',
                              fontSize: '0.86rem', cursor: 'pointer',
                              color: selectedLocation === sub.value ? '#2b6cb0' : '#4a5568',
                              fontWeight: selectedLocation === sub.value ? '600' : '400',
                              borderLeft: '2px solid #bee3f8', marginLeft: '1.25rem',
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7fafc'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {language === 'ru' ? sub.value : sub.label}
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>

              {/* Find attorney button */}
              <button
                type="button"
                onClick={handleSearch}
                disabled={!selectedArea || !selectedLocation}
                style={{
                  backgroundColor: (!selectedArea || !selectedLocation) ? 'rgba(255,255,255,0.18)' : '#e53e3e',
                  color: 'white',
                  border: 'none',
                  padding: isMobile ? '0.75rem 1.25rem' : '0.72rem 1.75rem',
                  borderRadius: isMobile ? '0 10px 10px 0' : '10px',
                  fontSize: isMobile ? '0.9rem' : '0.95rem',
                  fontWeight: '600',
                  cursor: (!selectedArea || !selectedLocation) ? 'not-allowed' : 'pointer',
                  boxShadow: (!selectedArea || !selectedLocation) ? 'none' : '0 4px 14px rgba(0,0,0,0.25)',
                  transition: 'background-color 0.2s',
                  whiteSpace: 'nowrap',
                  flex: '0 0 auto',
                }}
              >
                {isMobile ? 'Топиш →' : (language === 'ru' ? 'Найти адвоката →' : 'Адвокат топиш →')}
              </button>
            </div>

            {/* Hint */}
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', marginTop: '0.65rem' }}>
              {!selectedArea && !selectedLocation
                ? (language === 'ru' ? 'Выберите категорию и регион для поиска' : 'Категория ва ҳудудни танланг')
                : !selectedArea
                ? (language === 'ru' ? 'Выберите категорию' : 'Категорияни танланг')
                : (language === 'ru' ? 'Выберите регион' : 'Ҳудудни танланг')}
            </p>
          </div>

          {/* ── RIGHT: 3-step visual process ── */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', order: isMobile ? -1 : 0 }}>

            {/* grid: left col = tiles+connectors, right col = text locked to tile heights */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '54px 1fr' : '78px 1fr', columnGap: '1.25rem' }}>

              {/* Tiles + connectors */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {[
                  <svg key="s1" width={isMobile ? 32 : 46} height={isMobile ? 32 : 46} viewBox="0 0 52 52" fill="none">
                    <circle cx="18" cy="20" r="7" fill="rgba(255,255,255,0.9)"/>
                    <path d="M7 40 Q7 30 18 30 Q29 30 29 40" fill="rgba(255,255,255,0.9)"/>
                    <circle cx="30" cy="19" r="2" fill="rgba(255,255,255,0.55)"/>
                    <circle cx="35" cy="14" r="3" fill="rgba(255,255,255,0.7)"/>
                    <ellipse cx="43" cy="9" rx="8" ry="7" fill="rgba(255,255,255,0.9)"/>
                    <text x="43" y="13" textAnchor="middle" fontSize="10" fontWeight="800" fill="#e53e3e">?</text>
                  </svg>,
                  <svg key="s2" width={isMobile ? 32 : 46} height={isMobile ? 32 : 46} viewBox="0 0 52 52" fill="none">
                    <g transform="translate(4,3)">
                      <rect x="8" y="5" width="28" height="36" rx="4" fill="rgba(255,255,255,0.9)"/>
                      <line x1="14" y1="14" x2="30" y2="14" stroke="rgba(26,54,93,0.55)" strokeWidth="2.2" strokeLinecap="round"/>
                      <line x1="14" y1="20" x2="30" y2="20" stroke="rgba(26,54,93,0.3)" strokeWidth="2.2" strokeLinecap="round"/>
                      <line x1="14" y1="26" x2="24" y2="26" stroke="rgba(26,54,93,0.15)" strokeWidth="2.2" strokeLinecap="round"/>
                      <g transform="translate(30,28) rotate(-40)">
                        <rect x="-3" y="-10" width="6" height="14" rx="2" fill="rgba(255,210,80,0.95)"/>
                        <polygon points="-3,4 3,4 0,9" fill="rgba(255,180,40,0.9)"/>
                        <rect x="-3" y="-13" width="6" height="4" rx="1" fill="rgba(255,230,120,0.8)"/>
                      </g>
                    </g>
                  </svg>,
                  <svg key="s3" width={isMobile ? 32 : 46} height={isMobile ? 32 : 46} viewBox="0 0 52 52" fill="none">
                    <path d="M4 14 Q4 9 9 9 L32 9 Q37 9 37 14 L37 27 Q37 32 32 32 L18 32 L12 38 L12 32 L9 32 Q4 32 4 27 Z" fill="rgba(255,255,255,0.45)"/>
                    <path d="M15 20 Q15 15 20 15 L43 15 Q48 15 48 20 L48 33 Q48 38 43 38 L40 38 L40 44 L34 38 L20 38 Q15 38 15 33 Z" fill="rgba(229,62,62,0.9)"/>
                    <circle cx="26" cy="26.5" r="2.5" fill="white"/>
                    <circle cx="33" cy="26.5" r="2.5" fill="white"/>
                    <circle cx="40" cy="26.5" r="2.5" fill="white"/>
                    <rect x="26" y="2" width="14" height="10" rx="2" fill="rgba(255,255,255,0.85)"/>
                    <line x1="29" y1="5" x2="37" y2="5" stroke="rgba(26,54,93,0.4)" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="29" y1="8" x2="35" y2="8" stroke="rgba(26,54,93,0.4)" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>,
                ].map((svg, i) => (
                  <React.Fragment key={i}>
                    <div style={{
                      width: isMobile ? '54px' : '78px', height: isMobile ? '54px' : '78px',
                      borderRadius: isMobile ? '14px' : '20px', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: 'rgba(255,255,255,0.13)',
                      border: '1px solid rgba(255,255,255,0.22)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(12px)',
                    }}>{svg}</div>
                    {i < 2 && <div style={{ width: '2px', flex: 1, minHeight: '18px', backgroundColor: 'rgba(255,255,255,0.15)', margin: '6px 0' }} />}
                  </React.Fragment>
                ))}
              </div>

              {/* Text — each label locked to 78px tile height */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {[
                  { num: '01', label: language === 'ru' ? 'Нужен адвокат?' : 'Адвокат керакми?' },
                  { num: '02', label: language === 'ru' ? 'Выберите категорию' : 'Муаммоингизни белгиланг ва жўнатинг' },
                  { num: '03', label: language === 'ru' ? 'Свяжитесь с адвокатом' : 'Адвокатга боғланинг' },
                ].map((step, i) => (
                  <React.Fragment key={step.num}>
                    <div style={{ height: isMobile ? '54px' : '78px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ fontSize: '0.6rem', fontWeight: '700', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.14em', marginBottom: '0.2rem' }}>{step.num}</div>
                      <div style={{ fontSize: isMobile ? '1.25rem' : '1.7rem', fontWeight: '700', color: 'white', lineHeight: '1.2' }}>{step.label}</div>
                    </div>
                    {i < 2 && <div style={{ flex: 1, minHeight: '18px', margin: '6px 0' }} />}
                  </React.Fragment>
                ))}
              </div>

            </div>


          </div>
        </div>
      </section>

      {/* ══════════════ FIND ME SECTION ══════════════ */}
      <section style={{ padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', backgroundColor: 'white' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          <h2 style={{
            fontSize: isMobile ? '2.2rem' : '3.5rem',
            fontWeight: '800', color: '#1a365d',
            lineHeight: '1.2', marginBottom: '2.5rem', textAlign: 'center',
          }}>
            {language === 'ru' ? 'Пусть адвокат найдет вас' : 'Адвокат сизни топади'}
          </h2>

        </div>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* Step 1 — Practice area */}
            <div>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '0.5rem' }}>
                <span style={{ color: '#2b6cb0', fontWeight: '700', marginRight: '0.5rem' }}>1.</span>
                {language === 'ru' ? 'Выберите категорию' : 'Муаммоингизга яқинроғини танланг'}
              </label>
              <div ref={findMeAreaRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setFindMeAreaOpen(o => !o)}
                  style={{
                    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.85rem 1rem', borderRadius: '10px', cursor: 'pointer',
                    border: `1.5px solid ${findMeErrors.area ? '#e53e3e' : findMeArea ? '#3182ce' : '#e2e8f0'}`,
                    backgroundColor: 'white', fontSize: '1rem', color: findMeArea ? '#2d3748' : '#a0aec0',
                  }}
                >
                  <span>{findMeArea || (language === 'ru' ? 'Выберите категорию...' : 'Масалан, оила ҳуқуқи ...')}</span>
                  <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.5, flexShrink: 0, transform: findMeAreaOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {findMeAreaOpen && (
                  <div className="findme-area-dropdown" style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000,
                    backgroundColor: 'white', borderRadius: '10px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid #e2e8f0',
                    maxHeight: '300px', overflowY: 'auto', padding: '0.4rem 0',
                  }}>
                    <style>{`.findme-area-dropdown::-webkit-scrollbar{width:5px}.findme-area-dropdown::-webkit-scrollbar-thumb{background:#cbd5e0;border-radius:10px}`}</style>
                    {CASE_CATEGORIES.map((cat, ci) => (
                      <React.Fragment key={ci}>
                        <div
                          onClick={() => {
                            if (cat.subs.length === 0) { setFindMeArea(cat.label); setFindMeAreaOpen(false); setFindMeAreaExpanded(null); setFindMeErrors(e => ({ ...e, area: false })); }
                            else setFindMeAreaExpanded(p => p === ci ? null : ci);
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.92rem',
                            color: (findMeAreaExpanded === ci || findMeArea === cat.label) ? '#2b6cb0' : '#1a202c',
                            fontWeight: '600',
                            backgroundColor: (findMeAreaExpanded === ci || findMeArea === cat.label) ? '#ebf8ff' : 'transparent',
                          }}
                          onMouseEnter={e => { if (findMeAreaExpanded !== ci && findMeArea !== cat.label) e.currentTarget.style.backgroundColor = '#f7fafc'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = (findMeAreaExpanded === ci || findMeArea === cat.label) ? '#ebf8ff' : 'transparent'; }}
                        >
                          <span>{cat.label}</span>
                          {cat.subs.length > 0 && (
                            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="#a0aec0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                              style={{ transform: findMeAreaExpanded === ci ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                              <path d="M1 1l4 4 4-4"/>
                            </svg>
                          )}
                        </div>
                        {findMeAreaExpanded === ci && cat.subs.map((sub, si) => (
                          <div
                            key={si}
                            onClick={() => { setFindMeArea(sub); setFindMeAreaOpen(false); setFindMeAreaExpanded(null); setFindMeErrors(e => ({ ...e, area: false })); }}
                            style={{
                              padding: '0.45rem 1rem 0.45rem 1.9rem', cursor: 'pointer', fontSize: '0.86rem',
                              color: findMeArea === sub ? '#2b6cb0' : '#4a5568',
                              fontWeight: findMeArea === sub ? '600' : '400',
                              borderLeft: '2px solid #bee3f8', marginLeft: '1rem',
                              backgroundColor: findMeArea === sub ? '#ebf8ff' : 'transparent',
                            }}
                            onMouseEnter={e => { if (findMeArea !== sub) e.currentTarget.style.backgroundColor = '#f7fafc'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = findMeArea === sub ? '#ebf8ff' : 'transparent'; }}
                          >
                            {sub}
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Step 2 — Location */}
            <div>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '0.5rem' }}>
                <span style={{ color: '#2b6cb0', fontWeight: '700', marginRight: '0.5rem' }}>2.</span>
                {language === 'ru' ? 'Укажите вашу локацию' : 'Манзилингизни белгиланг'}
              </label>
              <div ref={findMeLocationRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setFindMeLocationOpen(o => !o)}
                  style={{
                    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.85rem 1rem', borderRadius: '10px', cursor: 'pointer',
                    border: `1.5px solid ${findMeErrors.location ? '#e53e3e' : findMeLocation ? '#3182ce' : '#e2e8f0'}`,
                    backgroundColor: 'white', fontSize: '1rem', color: findMeLocation ? '#2d3748' : '#a0aec0',
                  }}
                >
                  <span>
                    {findMeLocation
                      ? (REGION_KEY_BY_RU[findMeLocation]
                          ? t(`regions.${REGION_KEY_BY_RU[findMeLocation]}`)
                          : (() => { for (const subs of Object.values(REGION_SUBREGIONS)) { const m = subs.find(s => s.value === findMeLocation); if (m) return language === 'ru' ? m.value : m.label; } return findMeLocation; })())
                      : (language === 'ru' ? 'Выберите регион...' : 'Ҳудудни танланг...')}
                  </span>
                  <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.5, flexShrink: 0 }}>
                    <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {findMeLocationOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000,
                    backgroundColor: 'white', borderRadius: '10px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid #e2e8f0',
                    maxHeight: '280px', overflowY: 'auto', padding: '0.4rem 0',
                  }}>
                    {REGIONS.map(key => {
                      const subs = REGION_SUBREGIONS[key] || [];
                      const isExp = findMeLocationExpanded === key;
                      const sel = loc => { setFindMeLocation(loc); setFindMeLocationOpen(false); setFindMeLocationExpanded(null); setFindMeErrors(e => ({ ...e, location: false })); };
                      return (
                        <React.Fragment key={key}>
                          <div
                            onClick={() => subs.length ? setFindMeLocationExpanded(p => p === key ? null : key) : sel(REGION_LABELS_RU[key])}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.95rem',
                              color: (findMeLocation === REGION_LABELS_RU[key] || isExp) ? '#2b6cb0' : '#2d3748',
                              fontWeight: (findMeLocation === REGION_LABELS_RU[key] || isExp) ? '600' : '400',
                              backgroundColor: isExp ? '#ebf8ff' : 'transparent',
                            }}
                            onMouseEnter={e => { if (!isExp) e.currentTarget.style.backgroundColor = '#f7fafc'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = isExp ? '#ebf8ff' : 'transparent'; }}
                          >
                            <span>{t(`regions.${key}`)}</span>
                            {subs.length > 0 && (
                              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="#a0aec0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                style={{ transform: isExp ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                                <path d="M1 1l4 4 4-4"/>
                              </svg>
                            )}
                          </div>
                          {isExp && (
                            <>
                              <div onClick={() => sel(REGION_LABELS_RU[key])}
                                style={{ padding: '0.5rem 1rem 0.5rem 2rem', fontSize: '0.87rem', cursor: 'pointer',
                                  color: findMeLocation === REGION_LABELS_RU[key] ? '#2b6cb0' : '#4a5568',
                                  fontWeight: findMeLocation === REGION_LABELS_RU[key] ? '600' : '400',
                                  borderLeft: '2px solid #bee3f8', marginLeft: '1rem' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7fafc'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                {t(`regions.${key}`)} (барчаси)
                              </div>
                              {subs.map(sub => (
                                <div key={sub.value} onClick={() => sel(sub.value)}
                                  style={{ padding: '0.5rem 1rem 0.5rem 2rem', fontSize: '0.87rem', cursor: 'pointer',
                                    color: findMeLocation === sub.value ? '#2b6cb0' : '#4a5568',
                                    fontWeight: findMeLocation === sub.value ? '600' : '400',
                                    borderLeft: '2px solid #bee3f8', marginLeft: '1rem' }}
                                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7fafc'}
                                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                  {language === 'ru' ? sub.value : sub.label}
                                </div>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Step 3 — Phone */}
            <div>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#2d3748', marginBottom: '0.5rem' }}>
                <span style={{ color: '#2b6cb0', fontWeight: '700', marginRight: '0.5rem' }}>3.</span>
                {language === 'ru' ? 'Введите номер телефона' : 'Телефон рақамингизни киритинг'}
              </label>
              <PhoneInput
                value={findMePhone}
                onChange={val => { setFindMePhone(val); setFindMeErrors(er => ({ ...er, phone: false })); }}
                hasError={findMeErrors.phone}
              />
            </div>

            {/* Step 4 — Submit */}
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '0.75rem' }}>
                {language === 'ru'
                  ? <span>Отправьте запрос <span style={{ color: '#a0aec0' }}>(только адвокаты могут видеть)</span></span>
                  : <span>Сўровни юборинг <span style={{ color: '#a0aec0' }}>(фақат адвокатлар кўра олади)</span></span>
                }
              </div>
              {(findMeErrors.area || findMeErrors.location || findMeErrors.phone) && (
                <div style={{ fontSize: '0.85rem', color: '#e53e3e', marginBottom: '0.75rem' }}>
                  Барча майдонларни тўлдиринг
                </div>
              )}
              <button
                onClick={handleFindMe}
                style={{
                  width: '100%', padding: '1rem', borderRadius: '10px', border: 'none',
                  backgroundColor: '#1a365d', color: 'white',
                  fontSize: '1.05rem', fontWeight: '700', cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(26,54,93,0.3)',
                  transition: 'background-color 0.15s',
                }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#2a5298'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = '#1a365d'}
              >
                {language === 'ru' ? 'Пусть адвокат найдет меня →' : 'Адвокат мени топсин →'}
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* Success popup */}
      {findMeSuccess && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px',
            padding: '2.5rem 2rem', maxWidth: '420px', width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            textAlign: 'center',
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              backgroundColor: '#c6f6d5', display: 'flex',
              alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#276749" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <p style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1a365d', lineHeight: '1.6', marginBottom: '2rem' }}>
              {language === 'ru'
                ? 'Ваш запрос отправлен адвокатам. Ждите обратной связи от адвоката.'
                : 'Сўровингиз адвокатларга юборилди. Сизга алоқага чиқишларини кутинг.'
              }
            </p>
            <button
              onClick={() => { setFindMeSuccess(false); setFindMeArea(''); setFindMeLocation(''); setFindMePhone(''); }}
              style={{
                backgroundColor: '#1a365d', color: 'white', border: 'none',
                padding: '0.85rem 2.5rem', borderRadius: '10px',
                fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#2a5298'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = '#1a365d'}
            >
              {language === 'ru' ? 'Понятно' : 'Хўп, тушундим'}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════ LEGAL CONSULTATION ══════════════ */}
      <section style={{ padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', backgroundColor: '#f7fafc' }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>

          <h2 style={{
            fontSize: isMobile ? '1.6rem' : '2.2rem',
            fontWeight: '800',
            color: '#1a365d',
            lineHeight: '1.3',
            marginBottom: '2.5rem',
            textAlign: 'center',
          }}>
            {language === 'ru'
              ? 'Или же, возьмите платную консультацию. Мы — ваш адвокат!'
              : 'Ҳуқуқий маслахат олинг'
            }
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2.5rem' }}>
            {[
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2b6cb0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                ),
                text: language === 'ru' ? 'Зарегистрируйтесь' : 'Рўйхатдан ўтинг',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2b6cb0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                ),
                text: language === 'ru' ? 'Напишите свой вопрос' : 'Саволингизни ёзинг',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2b6cb0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                ),
                text: language === 'ru' ? 'Выполните платёж' : 'Тўловни амалга оширинг',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2b6cb0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                ),
                text: language === 'ru' ? 'Отправьте вопрос' : 'Саволингизни жўнатинг',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2b6cb0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                ),
                text: language === 'ru' ? 'Получите ответ от специалистов' : 'Мутахассислардан жавоб олинг',
              },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '1rem',
                backgroundColor: 'white', borderRadius: '12px',
                padding: '1rem 1.25rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '10px',
                  backgroundColor: '#ebf8ff', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {item.icon}
                </div>
                <span style={{ fontSize: '1rem', fontWeight: '600', color: '#2d3748' }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => navigate('/request-advice')}
              style={{
                backgroundColor: '#1a365d', color: 'white', border: 'none',
                padding: '0.9rem 2.5rem', borderRadius: '10px',
                fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(26,54,93,0.35)',
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#2a5298'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = '#1a365d'}
            >
              {language === 'ru' ? 'Получить консультацию →' : 'Маслахат олиш →'}
            </button>
          </div>

        </div>
      </section>

    </div>
  );
};

export default LandingPage;
